import logger from "../utils/logger";

export interface StreamUploadProgress {
  streamType: 'screen' | 'webcam';
  progress: number;
  loaded: number;
  total: number;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  error?: string;
}

export interface SeparateStreamUploadResult {
  screen?: { key: string; hasAudio: boolean; success: boolean };
  webcam?: { key: string; hasAudio: boolean; success: boolean };
}

type ProgressCallback = (progress: StreamUploadProgress) => void;

class SeparateStreamUploader {
  private abortControllers: Map<string, AbortController> = new Map();

  async uploadBlob(
    blob: Blob,
    presignedUrl: string,
    s3Key: string,
    streamType: 'screen' | 'webcam',
    onProgress?: ProgressCallback
  ): Promise<{ success: boolean; key: string }> {
    const uploadId = `${streamType}-${Date.now()}`;
    const abortController = new AbortController();
    this.abortControllers.set(uploadId, abortController);

    try {
      logger.info(`[SeparateStreamUploader] Starting ${streamType} upload`, {
        blobSize: blob.size,
        contentType: blob.type,
        key: s3Key.substring(0, 50) + '...',
      });

      onProgress?.({
        streamType,
        progress: 0,
        loaded: 0,
        total: blob.size,
        status: 'uploading',
      });

      const response = await this.uploadWithProgress(
        presignedUrl,
        blob,
        abortController.signal,
        (loaded, total) => {
          onProgress?.({
            streamType,
            progress: Math.round((loaded / total) * 100),
            loaded,
            total,
            status: 'uploading',
          });
        }
      );

      if (!response.ok) {
        throw new Error(`Upload failed with status: ${response.status}`);
      }

      logger.info(`[SeparateStreamUploader] ${streamType} upload completed`, {
        key: s3Key.substring(0, 50) + '...',
        status: response.status,
      });

      onProgress?.({
        streamType,
        progress: 100,
        loaded: blob.size,
        total: blob.size,
        status: 'completed',
      });

      return { success: true, key: s3Key };
    } catch (error: any) {
      const errorMessage = error.name === 'AbortError' 
        ? 'Upload cancelled' 
        : error.message || 'Unknown error';

      logger.error(`[SeparateStreamUploader] ${streamType} upload failed`, {
        error: errorMessage,
        key: s3Key.substring(0, 50) + '...',
      });

      onProgress?.({
        streamType,
        progress: 0,
        loaded: 0,
        total: blob.size,
        status: 'error',
        error: errorMessage,
      });

      return { success: false, key: s3Key };
    } finally {
      this.abortControllers.delete(uploadId);
    }
  }

  private async uploadWithProgress(
    url: string,
    blob: Blob,
    signal: AbortSignal,
    onProgress: (loaded: number, total: number) => void
  ): Promise<Response> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          onProgress(event.loaded, event.total);
        }
      });

      xhr.addEventListener('load', () => {
        resolve(new Response(null, { status: xhr.status, statusText: xhr.statusText }));
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Network error'));
      });

      xhr.addEventListener('abort', () => {
        reject(new Error('Upload aborted'));
      });

      signal.addEventListener('abort', () => {
        xhr.abort();
      });

      xhr.open('PUT', url);
      xhr.setRequestHeader('Content-Type', blob.type || 'video/webm');
      xhr.send(blob);
    });
  }

  async uploadStreams(
    streams: {
      screen?: { blob: Blob; url: string; key: string; hasAudio: boolean };
      webcam?: { blob: Blob; url: string; key: string; hasAudio: boolean };
    },
    onProgress?: ProgressCallback
  ): Promise<SeparateStreamUploadResult> {
    const results: SeparateStreamUploadResult = {};
    const uploadPromises: Promise<void>[] = [];

    if (streams.screen) {
      uploadPromises.push(
        this.uploadBlob(
          streams.screen.blob,
          streams.screen.url,
          streams.screen.key,
          'screen',
          onProgress
        ).then((result) => {
          results.screen = {
            key: result.key,
            hasAudio: streams.screen!.hasAudio,
            success: result.success,
          };
        })
      );
    }

    if (streams.webcam) {
      uploadPromises.push(
        this.uploadBlob(
          streams.webcam.blob,
          streams.webcam.url,
          streams.webcam.key,
          'webcam',
          onProgress
        ).then((result) => {
          results.webcam = {
            key: result.key,
            hasAudio: streams.webcam!.hasAudio,
            success: result.success,
          };
        })
      );
    }

    await Promise.all(uploadPromises);

    logger.info('[SeparateStreamUploader] All streams uploaded', {
      screenSuccess: results.screen?.success,
      webcamSuccess: results.webcam?.success,
    });

    return results;
  }

  abortAll(): void {
    this.abortControllers.forEach((controller, id) => {
      logger.info(`[SeparateStreamUploader] Aborting upload: ${id}`);
      controller.abort();
    });
    this.abortControllers.clear();
  }
}

export const separateStreamUploader = new SeparateStreamUploader();
